import React, { useState, useMemo } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useSnackbar from '../../../hooks/useSnackbar';
import FormGroup from '@mui/material/FormGroup';
import RolesAndPermissionsPicker from '../../../rolesAndPermissionsPicker/RolesAndPermissionsPicker';

const InviteUserDialog = ({ onSubmit, onClose }) => {
    const [emailAddress, setEmailAddress] = useState('');
    const [name, setName] = useState('');
    const [selectedRoleId, setSelectedRoleId] = useState(null);
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [emailTouched, setEmailTouched] = useState(false);
    const { showSnackbar } = useSnackbar();

    const emailPattern = /^[A-Za-z0-9\p{L}\p{N}!#$%&'+/=?^_{|}~-]+(?:\.[A-Za-z0-9\p{L}\p{N}!#$%&'*+/=?^_{|}~-]+)*@(?:[A-Za-z0-9\p{L}\p{N}.-]+)\.[A-Za-z\p{L}]{2,63}$/u;

    const isEmailValid = useMemo(() => emailPattern.test(emailAddress.trim()), [emailAddress]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isEmailValid) {
            setEmailTouched(true);
            showSnackbar('Please enter a valid email address', 'error');
            return;
        }

        try {
            setIsSubmitting(true);

            await onSubmit({
                name,
                emailAddress,
                roleId: selectedRoleId,
                permissions: selectedPermissions
            });

            setName('');
            setEmailAddress('');
            setSelectedRoleId(null);
            setSelectedPermissions([]);
            setEmailTouched(false);
            onClose?.();
        }
        catch (err) {
            showSnackbar(err?.message || 'Failed to invite user', 'error');
        }
        finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog onClose={onClose} open maxWidth="md" fullWidth>
            <form autoComplete="off" onSubmit={handleSubmit}>
                <DialogTitle>Invite User</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Invite another user to log into your NewZapp account.
                    </Typography>
                    <FormGroup>
                        <TextField
                            autoFocus
                            variant="outlined"
                            size="small"
                            label="Email Address"
                            type="email"
                            fullWidth
                            value={emailAddress}
                            onChange={(e) => {
                                setEmailAddress(e.target.value);
                                if (!emailTouched) setEmailTouched(true);
                            }}
                            onBlur={() => setEmailTouched(true)}
                            margin="dense"
                            required
                            placeholder="name@example.com"
                            error={emailTouched && !isEmailValid}
                            helperText={emailTouched && !isEmailValid ? 'Enter a valid email address' : null}
                        />
                        <TextField
                            size="small"
                            fullWidth
                            type="text"
                            variant="outlined"
                            label="Name"
                            autoComplete="off"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            margin="dense"
                        />
                    </FormGroup>
                    <RolesAndPermissionsPicker
                        selectedRoleId={selectedRoleId}
                        selectedPermissions={selectedPermissions}
                        setSelectedRoleId={setSelectedRoleId}
                        setSelectedPermissions={setSelectedPermissions}
                    />
                </DialogContent>

                <DialogActions>
                    <Button
                        type="submit"
                        disabled={!isEmailValid || isSubmitting}
                    >
                        {isSubmitting ? 'Inviting…' : 'Invite User'}
                    </Button>
                    <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
                        Cancel
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default InviteUserDialog;
